import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  Text,
  Avatar,
  Card,
  Badge,
  Loader,
  Center,
  ActionIcon,
  Tabs,
  Divider
} from '@mantine/core';
import {
  IconSearch,
  IconUserPlus,
  IconCheck,
  IconMail,
  IconUsers
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { AmigosService } from '../../services/amigosService';
import { MultijugadorService } from '../../services/multijugadorService';
import { BuscarUsuarioDTO, AmistadDTO } from '../../types/amigos';

interface InvitarUsuarioModalProps {
  opened: boolean;
  onClose: () => void;
  codigoSala: string;
  nombreSala: string;
}

const InvitarUsuarioModal: React.FC<InvitarUsuarioModalProps> = ({
  opened,
  onClose,
  codigoSala,
  nombreSala
}) => {
  const [activeTab, setActiveTab] = useState<string | null>('amigos');
  const [amigos, setAmigos] = useState<AmistadDTO[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState<BuscarUsuarioDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [invitando, setInvitando] = useState<number | null>(null);
  const [invitados, setInvitados] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (opened) {
      cargarAmigos();
    }
  }, [opened]);

  const cargarAmigos = async () => {
    setLoading(true);
    try {
      const amigosData = await AmigosService.obtenerAmigos();
      setAmigos(amigosData);
    } catch (error: any) {
      console.error('Error al cargar amigos:', error);
      notifications.show({
        title: 'Error',
        message: 'No se pudieron cargar tus amigos',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const buscarUsuarios = async () => {
    if (!busqueda.trim() || busqueda.length < 2) {
      notifications.show({
        title: 'Búsqueda inválida',
        message: 'Ingresa al menos 2 caracteres para buscar',
        color: 'orange',
      });
      return;
    }

    try {
      setBuscando(true);
      const resultados = await AmigosService.buscarUsuarios(busqueda);
      setResultadosBusqueda(resultados);

      if (resultados.length === 0) {
        notifications.show({
          title: 'Sin resultados',
          message: 'No se encontraron usuarios con ese nombre',
          color: 'blue',
        });
      }
    } catch (error: any) {
      console.error('Error al buscar usuarios:', error);
      notifications.show({
        title: 'Error al buscar',
        message: error.message,
        color: 'red',
      });
    } finally {
      setBuscando(false);
    }
  };

  const invitarUsuario = async (usuarioId: number, nombreUsuario: string) => {
    try {
      setInvitando(usuarioId);
      await MultijugadorService.invitarUsuario(codigoSala, usuarioId);

      notifications.show({
        title: '¡Invitación enviada!',
        message: `Se envió una invitación a ${nombreUsuario}`,
        color: 'green',
        icon: <IconCheck size={18} />,
      });

      // Agregar al set de invitados
      setInvitados(prev => new Set(prev).add(usuarioId));
    } catch (error: any) {
      console.error('Error al invitar usuario:', error);
      notifications.show({
        title: 'Error al invitar',
        message: error.message,
        color: 'red',
      });
    } finally {
      setInvitando(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      buscarUsuarios();
    }
  };

  const handleClose = () => {
    setBusqueda('');
    setResultadosBusqueda([]);
    setInvitados(new Set());
    setActiveTab('amigos');
    onClose();
  };

  const renderUsuarioCard = (
    usuario: { id: number; nombre?: string; apellidos?: string; username: string; avatarUrl?: string },
    esAmigo: boolean = false
  ) => {
    const yaInvitado = invitados.has(usuario.id);
    const nombreCompleto = usuario.nombre && usuario.apellidos
      ? `${usuario.nombre} ${usuario.apellidos}`
      : usuario.username;

    return (
      <Card key={usuario.id} withBorder padding="md">
        <Group justify="space-between">
          <Group gap="md">
            <Avatar
              src={usuario.avatarUrl}
              size="md"
              color="blue"
            >
              {(usuario.nombre || usuario.username).charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <Text fw={500} size="sm">
                {nombreCompleto}
              </Text>
              <Text size="xs" color="dimmed">
                @{usuario.username}
              </Text>
              {esAmigo && (
                <Badge size="xs" variant="light" color="green" mt={4}>
                  Amigo
                </Badge>
              )}
            </div>
          </Group>

          {/* Botón de invitar */}
          {yaInvitado ? (
            <Badge variant="light" color="green" leftSection={<IconCheck size={14} />}>
              Invitado
            </Badge>
          ) : (
            <ActionIcon
              variant="filled"
              color="blue"
              size="lg"
              onClick={() => invitarUsuario(usuario.id, nombreCompleto)}
              loading={invitando === usuario.id}
            >
              <IconMail size={18} />
            </ActionIcon>
          )}
        </Group>
      </Card>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconUserPlus size={24} />
          <Text fw={600}>Invitar jugadores a la sala</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {/* Información de la sala */}
        <Card withBorder padding="sm" className="bg-blue-50">
          <Text size="sm" color="dimmed">Sala:</Text>
          <Text fw={600}>{nombreSala}</Text>
          <Text size="xs" color="dimmed">Código: {codigoSala}</Text>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="amigos" leftSection={<IconUsers size={16} />}>
              Mis Amigos ({amigos.length})
            </Tabs.Tab>
            <Tabs.Tab value="buscar" leftSection={<IconSearch size={16} />}>
              Buscar Usuarios
            </Tabs.Tab>
          </Tabs.List>

          {/* Tab de Amigos */}
          <Tabs.Panel value="amigos" pt="md">
            {loading ? (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <Loader size="md" />
                  <Text size="sm" color="dimmed">Cargando amigos...</Text>
                </Stack>
              </Center>
            ) : amigos.length === 0 ? (
              <Center py="xl">
                <Stack align="center" gap="xs">
                  <IconUsers size={48} color="gray" />
                  <Text color="dimmed">Aún no tienes amigos</Text>
                  <Text size="sm" color="dimmed">
                    Ve a la pestaña "Buscar Usuarios" para encontrar amigos
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Stack gap="xs">
                {amigos.map((amistad) =>
                  renderUsuarioCard(
                    {
                      id: amistad.amigo.id,
                      nombre: amistad.amigo.nombre,
                      apellidos: amistad.amigo.apellidos,
                      username: amistad.amigo.username,
                      avatarUrl: amistad.amigo.avatarUrl
                    },
                    true
                  )
                )}
              </Stack>
            )}
          </Tabs.Panel>

          {/* Tab de Búsqueda */}
          <Tabs.Panel value="buscar" pt="md">
            <Stack gap="md">
              {/* Buscador */}
              <TextInput
                placeholder="Buscar por nombre o usuario..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.currentTarget.value)}
                onKeyPress={handleKeyPress}
                rightSection={
                  <ActionIcon
                    onClick={buscarUsuarios}
                    loading={buscando}
                    variant="filled"
                    color="blue"
                  >
                    <IconSearch size={18} />
                  </ActionIcon>
                }
              />

              {/* Resultados de búsqueda */}
              {buscando ? (
                <Center py="xl">
                  <Stack align="center" gap="sm">
                    <Loader size="md" />
                    <Text size="sm" color="dimmed">Buscando usuarios...</Text>
                  </Stack>
                </Center>
              ) : resultadosBusqueda.length > 0 ? (
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Resultados ({resultadosBusqueda.length})
                  </Text>
                  {resultadosBusqueda.map((usuario) =>
                    renderUsuarioCard(
                      {
                        id: usuario.id,
                        nombre: usuario.nombre,
                        apellidos: usuario.apellidos,
                        username: usuario.username,
                        avatarUrl: usuario.avatarUrl
                      },
                      usuario.estadoRelacion === 'AMIGOS'
                    )
                  )}
                </Stack>
              ) : busqueda.length >= 2 && !buscando ? (
                <Center py="xl">
                  <Stack align="center" gap="xs">
                    <Text color="dimmed">No se encontraron usuarios</Text>
                  </Stack>
                </Center>
              ) : (
                <Center py="xl">
                  <Text size="sm" color="dimmed">
                    Busca usuarios por nombre o username
                  </Text>
                </Center>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Divider />

        {/* Botón cerrar */}
        <Group justify="flex-end">
          <Button variant="outline" onClick={handleClose}>
            Cerrar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default InvitarUsuarioModal;
